import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import type { CreateAdvertiserPayload } from "../../../../api/ads";

import "./CreateAdvertiserModal.css";

export interface CreateAdvertiserModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateAdvertiserPayload) => Promise<void>;
  submitting: boolean;
  error?: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = { name: "", contactEmail: "" };

export const CreateAdvertiserModal: React.FC<CreateAdvertiserModalProps> = ({
  show,
  onClose,
  onSubmit,
  submitting,
  error,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [validationErrors, setValidationErrors] = useState<{ name?: string; contactEmail?: string }>({});

  useEffect(() => {
    if (show) {
      setForm(emptyForm);
      setValidationErrors({});
    }
  }, [show]);

  const validate = (): boolean => {
    const errors: { name?: string; contactEmail?: string } = {};
    if (!form.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!form.contactEmail.trim()) {
      errors.contactEmail = "Contact email is required.";
    } else if (!EMAIL_REGEX.test(form.contactEmail.trim())) {
      errors.contactEmail = "Please enter a valid email address.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit({ name: form.name.trim(), contactEmail: form.contactEmail.trim() });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="ca-modal-dialog"
      contentClassName="ca-modal-content"
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="ca-modal__header">
          <span className="ca-modal__title">Create Advertiser</span>
          <button type="button" className="ca-modal__close" onClick={onClose} aria-label="Close" disabled={submitting}>
            <i className="ph-x" />
          </button>
        </div>

        <div className="ca-modal__body">
          {error && <p className="ca-modal__api-error">{error}</p>}

          <div className="ca-modal__field">
            <label className="ca-modal__label" htmlFor="ca-name">
              Name <span className="ca-modal__required">*</span>
            </label>
            <input
              id="ca-name"
              type="text"
              className={`ca-modal__input${validationErrors.name ? " ca-modal__input--error" : ""}`}
              placeholder="e.g. Acme Sports Equipment"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              disabled={submitting}
              autoComplete="off"
            />
            {validationErrors.name && <p className="ca-modal__field-error">{validationErrors.name}</p>}
          </div>

          <div className="ca-modal__field">
            <label className="ca-modal__label" htmlFor="ca-email">
              Contact Email <span className="ca-modal__required">*</span>
            </label>
            <input
              id="ca-email"
              type="email"
              className={`ca-modal__input${validationErrors.contactEmail ? " ca-modal__input--error" : ""}`}
              placeholder="e.g. advertiser@example.com"
              value={form.contactEmail}
              onChange={(e) => setForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
              disabled={submitting}
              autoComplete="off"
            />
            {validationErrors.contactEmail && <p className="ca-modal__field-error">{validationErrors.contactEmail}</p>}
          </div>
        </div>

        <div className="ca-modal__actions">
          <button type="button" className="ca-modal__btn ca-modal__btn--cancel" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="ca-modal__btn ca-modal__btn--confirm" disabled={submitting}>
            {submitting ? "Creating..." : "Create Advertiser"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
